"use client";
import Image from "next/image";
import Reveal from "../../components/Reveal";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";

export default function WhyInvestSection() {
  const t = useTranslations("invester");
  const dataTable = [
    {
      aspect: t("ownership"),
      centralized: t("infrastructure-controlled-by-big-tech-vendor-lock-in"),
      hyraDecentralizedL3: t(
        "infrastructure-owned-by-the-community-dao-government-agencies-can-run-their-own-nodes",
      ),
    },
    {
      aspect: t("cost"),
      centralized: t("rising-gpu-rental-costs-unsustainable"),
      hyraDecentralizedL3: t(
        "distributed-compute-leveraging-idle-gpus-worldwide-lower-cost",
      ),
    },
    {
      aspect: t("transparency"),
      centralized: t("black-box-ai-little-verifiability"),
      hyraDecentralizedL3: t("verifiable-compute-via-zero-knowledge-proofs"),
    },
    {
      aspect: t("compliance"),
      centralized: t(
        "hard-to-comply-when-dependent-on-servers-in-other-countries",
      ),
      hyraDecentralizedL3: t(
        "process-data-locally-on-premise-national-nodes-data-sovereignty",
      ),
    },
    {
      aspect: t("value-capture"),
      centralized: t("profits-concentrate-with-big-tech"),
      hyraDecentralizedL3: t(
        "users-builders-validators-governments-share-the-value",
      ),
    },
  ];

  return (
    <section className="mx-auto max-w-[1440px] px-[78px] py-[120px] max-lg:px-10 max-sm:px-4">
      <div className="text-center">
        <Reveal
          delay={0.1}
          duration={0.8}
          direction="left"
          withBlur={true}
          blurAmount={8}
        >
          <h2 className="text-[48px]/[56px] tracking-[-1px] max-lg:text-[32px]/[40px]">
            <span className="text-[#06f]">{t("why-invest")}</span>{" "}
            {t("in-hyra-network")}?
          </h2>
        </Reveal>
        <Reveal
          delay={0.5}
          duration={0.8}
          direction="left"
          withBlur={true}
          blurAmount={8}
        >
          <p className="mt-6 text-lg font-light text-[#646464] max-lg:text-base">
            {t(
              "the-first-ever-layer-3-specialized-for-decentralized-ai-and-depin",
            )}
          </p>
        </Reveal>
      </div>

      {/* desktop */}
      <motion.div
        initial={{ opacity: 0, translateY: 40 }}
        whileInView={{ opacity: 1, translateY: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
        viewport={{ once: true }}
        className="max-md:hidden"
      >
        <div className="mt-14 w-full rounded-3xl border-[2px] border-[#E9F3FF] bg-[#F3F8FF]/[38%] px-6">
          <table className="">
            <thead className="text-left">
              <tr className="text-lg tracking-tight">
                <th className="w-[258px] py-[30px] font-normal">
                  {t("aspect")}
                </th>
                <th className="px-6 py-[30px] font-normal">
                  {t("centralized-big-tech-cloud")}
                </th>
                <th className="px-6 py-[30px] font-normal">
                  {t("hyras-decentralized-l3")}
                </th>
              </tr>
            </thead>
            <tbody className="text-left">
              {dataTable.map((data, index) => (
                <tr key={index} className="border-t border-[#E9F3FF] text-base">
                  <td className="font-light text-[#06f]">{data.aspect}</td>
                  <td className="px-6 font-light">{data.centralized}</td>
                  <td className="px-6">
                    <div className="flex min-h-[84px] items-center gap-2">
                      <Image
                        src={"/images/icons/check-circle.svg"}
                        width={20}
                        height={20}
                        alt="check-logo"
                      />
                      <span className="whitespace-pre-line font-light">
                        {data.hyraDecentralizedL3}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* tablet - mobile */}
      <div className="mt-12 grid grid-cols-1 gap-3 md:hidden">
        <div className="flex text-center">
          <h5 className="w-1/2 text-base">Centralized (Big Tech Cloud)</h5>
          <h5 className="w-1/2 text-base">Hyras Decentralized L3</h5>
        </div>
        {dataTable.map((item, i) => (
          <div key={i} className="flex gap-3">
            {/* Left card */}
            <div className="w-1/2 rounded-xl bg-[#F9F9F9B2] p-3">
              <p className="text-xs font-light text-[#06f]">{item.aspect}</p>
              <p className="mt-4 text-sm">{item.centralized}</p>
            </div>
            {/* Right card */}
            <div className="w-1/2 rounded-xl bg-[#F3F8FFC7] p-3">
              <p className="text-xs font-light text-[#06f]">{item.aspect}</p>
              <p className="mt-4 text-sm">{item.hyraDecentralizedL3}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
